import classNames from 'classnames'
import Link from 'next/link'

import Icon from '@/components/Icon/Icon'
import InteractiveTooltip from '@/components/InteractiveTooltip/InteractiveTooltip'
import Mana from '@/components/Mana/Mana'
import { CardLabelVariantType, GenericCardType, PageCardType } from '@/types/card'

import styles from './CardLabel.module.scss'

const LINK_FAQ_SYNERGY = '/faq?q=1'
const LINK_ARTICLE_LIFT = '/articles/changelog-replacing-synergy-with-lift-on-edhrecs-card-pages'

const formatSigFig = (num: number) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(2).replace(/\.0+$/, '') + 'M'

  if (num >= 100_000) return Math.round(num / 1_000) + 'K'

  if (num >= 10_000) return (num / 1_000).toFixed(1).replace(/\.0+$/, '') + 'K'

  if (num >= 1_000) return (num / 1_000).toFixed(2).replace(/\.0+$/, '') + 'K'

  return num.toString()
}

export const formatLift = (lift: number) => {
  const numerals = 2
  const liftRounded = Math.round(lift)
  let decimals = liftRounded >= 100 ? numerals - 3 : liftRounded >= 10 ? numerals - 2 : numerals - 1
  decimals = decimals > 0 ? decimals : 0
  return lift.toFixed(decimals)
}

const formatSynergy = (synergy: number) => `${(synergy * 100).toFixed(0)}%`

const formatInclusion = (numDecks: number, potentialDecks: number, numeralsInput: number = 2) => {
  if (potentialDecks <= 0) return null
  const numerals = numeralsInput >= 2 && numeralsInput <= 5 ? numeralsInput : 2
  const inclusion = (numDecks / potentialDecks) * 100
  const inclusionRounded = Math.round(inclusion)
  let decimals = inclusionRounded >= 100 ? numerals - 3 : inclusionRounded >= 10 ? numerals - 2 : numerals - 1
  decimals = decimals > 0 ? decimals : 0

  return `${inclusion.toFixed(decimals)}%`
}

const formatUnpopularity = (unpopularity: number) => `${(unpopularity * 100).toFixed(1)}%`

const CardLabelSalt = ({ card }: { card?: GenericCardType }) => {
  const salt = (card as any)?.salt

  if (!salt) return null

  return (
    <div className={styles.salt}>
      <span>Salt Score: {salt.toFixed(2)}</span>
    </div>
  )
}

const CardLabelTotal = ({ card, prefix, suffix }: { card?: GenericCardType; prefix?: string; suffix?: string }) => {
  const numDecks = card?.num_decks

  if (numDecks === undefined) return null

  return (
    <div className={styles.line}>
      {prefix && <span>{prefix}</span>}
      <span>{numDecks.toLocaleString()}</span>
      {suffix && <span>{suffix}</span>}
      <span>decks</span>
    </div>
  )
}

const CardLabelInclusion = ({
  card,
  numerals,
  placement = 'bottom',
}: {
  card?: GenericCardType
  numerals?: 2 | 3
  placement?: 'top' | 'bottom'
}) => {
  const numDecks = card?.num_decks
  const potentialDecks = card?.potential_decks

  if (numDecks === undefined || potentialDecks === undefined) return null

  const inclusion = formatInclusion(numDecks, potentialDecks, numerals)

  const numDecksFormatted = formatSigFig(numDecks)
  const potentialDecksFormatted = formatSigFig(potentialDecks)

  return potentialDecks > 0 ? (
    <InteractiveTooltip
      placement={placement}
      tooltipChildren={
        <div className={styles.line}>
          <span className={classNames(styles.fraction)}>
            <span>
              <strong>{numDecks.toLocaleString()}</strong> decks
            </span>
            <span className={styles.denominator}>
              <strong>{potentialDecks.toLocaleString()}</strong> potential decks
            </span>
          </span>
        </div>
      }
    >
      <div className={styles.line}>
        <span className={styles.stat}>{inclusion}</span>
        <span className={styles.label}>inclusion</span>
        <span>
          <span className={classNames(styles.fraction, 'ms-2')}>
            <span className={styles.numerator}>
              <b>{numDecksFormatted}</b> decks
            </span>
            <span className={styles.denominator}>
              <b>{potentialDecksFormatted}</b> decks
            </span>
          </span>
        </span>
      </div>
    </InteractiveTooltip>
  ) : (
    <div className={styles.line}>
      <span className={styles.stat}>0</span>
      <span className={styles.label}>decks</span>
    </div>
  )
}

const CardLabelRank = ({ card }: { card?: GenericCardType }) => {
  const rank = card?.rank?.toLocaleString()
  if (rank === undefined) return null

  return (
    <div className={styles.line}>
      <span className={styles.rank}>Rank #{rank}</span>
    </div>
  )
}

const CardLabelLift = ({
  card,
  forceSynergy,
  pageCommander,
}: {
  card?: PageCardType
  forceSynergy?: boolean
  pageCommander?: PageCardType
}) => {
  if (card === undefined) return null

  const lift = card.lift

  if (lift !== undefined && (!forceSynergy || card.synergy === undefined)) {
    const liftFormatted = formatLift(lift)

    // Formatting rounds & truncates
    // The formatted number is used to determine the icon
    const liftFormattedFloat = parseFloat(liftFormatted)
    const icon =
      liftFormattedFloat >= 10
        ? 'anglesUp'
        : liftFormattedFloat >= 1.25
          ? 'angleUp'
          : liftFormattedFloat >= 0.8
            ? false
            : liftFormattedFloat >= 0.3
              ? 'angleDown'
              : 'anglesDown'

    return (
      <InteractiveTooltip
        placement='bottom'
        tooltipChildren={
          <div className={styles.liftTooltipBody}>
            <span>Lift =&nbsp;</span>
            <span className={classNames(styles.fraction)}>
              <span>
                <strong>{formatInclusion(card.num_decks, card.potential_decks, 2)}</strong> inclusion
              </span>
              <span className={classNames(styles.denominator, 'd-flex align-items-center')}>
                <strong>{((100 * card.num_decks) / (card.potential_decks * lift)).toFixed(2)}%</strong>
                &nbsp;inclusion in potential decks
              </span>
            </span>
            <span>
              &nbsp;= <strong>{lift.toFixed(1)}</strong>
            </span>
          </div>
        }
      >
        <Link className={styles.line} href={LINK_ARTICLE_LIFT} target='_blank'>
          {icon && (
            <span className={styles.iconLeft}>
              <Icon icon={icon} />
            </span>
          )}
          <span>
            <span className={styles.statSmall}>{liftFormatted}</span>
            <span>x</span>
          </span>
          <span className={classNames(styles.label, styles.underline)}>lift</span>
        </Link>
      </InteractiveTooltip>
    )
  }

  // Fallback to Synergy if Lift is unavailable
  const synergy = card?.synergy
  const numDecks = card?.num_decks
  const potentialDecks = card?.potential_decks

  if (synergy === undefined || numDecks === undefined || potentialDecks === undefined) return null

  const synergyFormatted = formatSynergy(synergy)
  let arrowsUp = 0
  if (synergy > 0.6) arrowsUp = 2
  else if (synergy > 0.2) arrowsUp = 1

  const arrows = Array(arrowsUp).fill(<Icon icon='arrowUp' />)

  let content = (
    <Link className={styles.line} href={LINK_FAQ_SYNERGY} target='_blank'>
      {arrows && <span className={styles.iconLeft}>{arrows}</span>}
      <span className={styles.statSmall}>{synergyFormatted}</span>
      <span className={classNames(styles.label, styles.underline)}>synergy</span>
    </Link>
  )

  if (pageCommander !== undefined) {
    content = (
      <InteractiveTooltip
        placement='bottom'
        tooltipChildren={
          <Link className={styles.synergyTooltipBody} href={LINK_FAQ_SYNERGY} target='_blank'>
            <span>synergy = </span>
            <span>
              <div>
                usage in {pageCommander.name.split(' // ')[0]} ({Math.round((100 * numDecks) / potentialDecks)}
                %)
              </div>
              <div>
                - usage in <Mana colors={pageCommander.color_identity} /> (
                {Math.round(Math.round((100 * numDecks) / potentialDecks) - 100 * synergy)}
                %)
              </div>
            </span>
          </Link>
        }
      >
        {content}
      </InteractiveTooltip>
    )
  }

  return content
}

const CardLabelAddedTo = ({ card }: { card?: GenericCardType }) => {
  if (card?.num_decks === undefined || card?.potential_decks === undefined) return null

  return (
    <div className={styles.line}>
      Added to <b>{formatInclusion(card.num_decks, card.potential_decks, 3)}</b> of decks
    </div>
  )
}

const CardLabelCutFrom = ({ card }: { card?: GenericCardType }) => {
  if (card?.unpopularity === undefined) return null

  return (
    <div className={styles.line}>
      Cut from <b>{formatUnpopularity(card.unpopularity)}</b> of decks
    </div>
  )
}

type Props = {
  card?: PageCardType
  labelVariant?: CardLabelVariantType
  pageCommander?: PageCardType
}
const getLabelContent = (card?: PageCardType, labelVariant?: CardLabelVariantType, pageCommander?: PageCardType) => {
  switch (labelVariant) {
    case 'directory-count':
      return (
        <>
          <CardLabelTotal card={card} />
        </>
      )
    case 'directory-inclusion':
      return (
        <>
          <CardLabelInclusion card={card} />
        </>
      )
    case 'directory-rank':
      return (
        <>
          <CardLabelRank card={card} />
          <CardLabelTotal card={card} />
        </>
      )
    case 'directory-tags':
      return (
        <>
          <CardLabelTotal card={card} suffix={card?.name} />
        </>
      )
    case 'featured':
      return pageCommander?.is_commander ? (
        <>
          <CardLabelRank card={card} />
          <CardLabelTotal card={card} />
        </>
      ) : (
        <>
          <CardLabelInclusion card={card} numerals={3} />
        </>
      )
    case 'name':
      return <div className={styles.defaultLabel}>{card?.name}</div>
    case 'precon-add':
      return <CardLabelAddedTo card={card} />
    case 'precon-cut':
      return <CardLabelCutFrom card={card} />
    case 'rec':
    case 'rec-synergy':
      return card?.num_decks !== undefined && card?.potential_decks !== undefined ? (
        <>
          <CardLabelInclusion card={card} placement='top' />
          <CardLabelLift card={card} forceSynergy={labelVariant === 'rec-synergy'} pageCommander={pageCommander} />
        </>
      ) : (
        <CardLabelTotal card={card} />
      )
    case 'salt':
      return (
        <>
          <CardLabelSalt card={card} />
          <CardLabelTotal card={card} />
        </>
      )
    default:
      return card?.label ? <div className={styles.defaultLabel}>{card.label}</div> : null
  }
}

const CardLabel = ({ card, labelVariant, pageCommander }: Props) => {
  return <div className={styles.container}>{getLabelContent(card, labelVariant, pageCommander)}</div>
}

export default CardLabel
